import React, { useCallback, useEffect, useRef, useState } from "react";
import { auth } from "../../../config/firebaseConfig";
import { Link, useOutletContext } from "react-router-dom";
import html2canvas from "html2canvas";
import { FaClipboardCheck, FaShare } from "react-icons/fa6";
import { IoCopy } from "react-icons/io5";
import { QRCodeSVG } from "qrcode.react";
import { FiDownload } from "react-icons/fi";
import { IoWarningOutline } from "react-icons/io5";
import SchoolLogo from "./SchoolLogo";
import { sendVerificationEmail } from "../dashboardFunctions";
import { LuLoader2 } from "react-icons/lu";
// import { PDFDownloadLink } from "@react-pdf/renderer";
// import PDFContentSchool from "../pdf/PDFContentSchool";
// import DashboardInstructions from "../parent/DashboardIns";
import { GAEventTrack } from "../../../utils/hooks/GA_events";
import envConfig from "../../../config/envConfig";
import { useSelector } from "react-redux";
import AppStoreButtons from "../../../components/AppStoreButtons";

const SchoolDashboard = () => {
  const [verificationEmailSent, setVerificationEmailSent] = useState(null);
  const [sendVerifLoading, setSendVerifLoading] = useState(false);
  const [registrationCode, setRegistrationCode] = useState(null);

  const { schoolDetails, totalFeeCollected } = useOutletContext();
  // const { participants } = useSelector((state) => state.schoolParticipants);

  useEffect(() => {
    schoolDetails &&
      setRegistrationCode(
        `${envConfig.appUrl}register/individual/${schoolDetails?.id}`
      );
  }, [schoolDetails]);

  // Calculate QR code size based on container dimensions
  useEffect(() => {
    const calculateQrSize = () => {
      if (qrContainerRef.current) {
        const container = qrContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const padding = 32; // 16px padding on each side (p-4)
        const availableSize =
          Math.min(containerRect.width, containerRect.height) - padding;
        const optimalSize = Math.max(120, Math.min(availableSize, 300));
        setQrSize(optimalSize);
      }
    };

    calculateQrSize();
    window.addEventListener("resize", calculateQrSize);

    return () => window.removeEventListener("resize", calculateQrSize);
  }, [schoolDetails]);

  const [copied, setCopied] = useState(false);
  const regCodeRef = useRef(null);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(registrationCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);

      const range = document.createRange();
      range.selectNodeContents(regCodeRef.current);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      //GA event tracking
      GAEventTrack({
        event: "sch_dshbrd_copy_reg_code",
        data: {
          uid: auth?.currentUser?.uid,
          email: auth?.currentUser?.email,
        },
      });
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Link to Register for Students",
          url: registrationCode,
        });

        //GA event tracking
        GAEventTrack({
          event: "sch_dshbrd_share_reg_code",
          data: {
            uid: auth?.currentUser?.uid,
            email: auth?.currentUser?.email,
          },
        });
      } catch (error) {
        console.log("Error sharing link: ", error);
      }
    } else {
      console.log("Web Share API not supported");
    }
  };

  const shareViaWhatsApp = () => {
    const message = `Register for Practido – Olympiad Practice App
Get a special offer – only ₹${schoolDetails?.fee.total} for this academic year (worth ₹1499)!\n
  • Covers Math, Science, English & Computers
  • For Classes 1 to 8
  • Fun gamified learning with trophies & leaderboards
  • Instant results & progress tracking
   \n
Use this link: ${registrationCode}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");

    //GA event tracking
    GAEventTrack({
      event: "sch_dshbrd_share_whatsapp",
      data: {
        uid: auth?.currentUser?.uid,
        email: auth?.currentUser?.email,
      },
    });
  };

  const shareViaEmail = () => {
    const subject =
      "Download Practido - Olympiad Practice App today(Special Offer)";
    const body = `Dear Parents and Students,

We are excited to share a special opportunity for your child's Olympiad preparation.

With Practido - Olympiad Practice App, students can:
• Practice unlimited questions in Math, Science, Computers, and English (Class 1-8)
• Learn with instant solutions and progress tracking
• Stay engaged with gamified challenges that make learning fun
• Practice anytime, anywhere - no running out of material

Special Academic Year Offer:
Your child can now access the app for just a one-time fee of ₹${schoolDetails?.fee?.total} per student for this academic year.

👉 Register here: ${registrationCode}

Download Practido App:
• Android App: https://play.google.com/store/apps/details?id=com.naskay.olympiadpractice
• iOS App: https://apps.apple.com/in/app/practido-olympiad-practice/id6746647468

For any help, you can directly email - hello@practido.com
or connect on WhatsApp - +91 84485 04381

Give your child the extra practice to shine in Olympiads this year!`;
    const emailUrl = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    window.open(emailUrl);

    //GA event tracking
    GAEventTrack({
      event: "sch_dshbrd_share_email",
      data: {
        uid: auth?.currentUser?.uid,
        email: auth?.currentUser?.email,
      },
    });
  };

  const qrRef = useRef();
  const qrContainerRef = useRef();
  const [qrHover, setQrHover] = useState(false);
  const [qrSize, setQrSize] = useState(140);
  const downloadQRCode = useCallback(() => {
    html2canvas(qrRef.current, {
      useCORS: true,
      logging: true,
    }).then((canvas) => {
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `unique-student-registration-qrcode.png`;
      link.click();

      //GA event tracking
      GAEventTrack({
        event: "sch_dshbrd_copy_download_qrcode",
        data: {
          uid: auth?.currentUser?.uid,
          email: auth?.currentUser?.email,
        },
      });
    });
  }, [qrRef]);

  // const shareQRCode = async () => {
  //   if (navigator.share) {
  //     try {
  //       await navigator.share({
  //         title: "QR Code to Register for Students",
  //         url: "",
  //       });
  //     } catch (error) {
  //       console.log("Error sharing link: ", error);
  //     }
  //   } else {
  //     console.log("Web Share API not supported");
  //   }
  // };

  return (
    <div className="container space-y-5">
      {schoolDetails?.emailVerified === false && (
        <div className="bg-[#FFDBD2] text-[#FF0707] p-3 flex gap-2 items-center justify-center rounded-xl">
          <IoWarningOutline className="w-5 h-5 " />
          <span className="text-base">
            Please verify your email address to unlock more features and
            services. We've sent a verification link to your registered email.
          </span>
          {verificationEmailSent ? (
            <span className="font-bold">✓ Sent</span>
          ) : (
            <button
              className="font-extrabold hover:underline flex items-center gap-2"
              onClick={() => {
                sendVerificationEmail({
                  actionUrl: "schoolDashboard",
                  setLoading: setSendVerifLoading,
                  setVerificationEmailSent: setVerificationEmailSent,
                });
                //GA event tracking
                GAEventTrack({
                  event: "school_dashboard_verif_email_sent",
                  data: {
                    uid: auth?.currentUser?.uid,
                    email: auth?.currentUser?.email,
                  },
                });
              }}
            >
              Resend Link
              {sendVerifLoading && (
                <span>
                  <LuLoader2 className="animate-spin" />
                </span>
              )}
            </button>
          )}
        </div>
      )}
      <div className="school-logo-row flex items-center justify-center mb-4">
        {!schoolDetails ? (
          <div className="col-span-2 flex items-center justify-center gap-5">
            <div className="bg-gray-400 w-24 h-24 rounded-full flex-shrink-0" />

            <div className="flex flex-col gap-1">
              <div className="w-60 h-8 bg-gray-300 rounded-lg animate-pulse" />
              <div className="w-40 h-4 bg-gray-300 rounded-lg animate-pulse" />
              <div className="w-48 h-4 bg-gray-300 rounded-lg animate-pulse" />
            </div>
          </div>
        ) : (
          <div>
            <SchoolLogo schoolDetails={schoolDetails} />
          </div>
        )}

        {/* <div className="practido-log">
          <img
            className="banner-cloud w-[360px]"
            src="/img/new-home/cloud-banner-common.png"
            alt="banner"
          />
        </div> */}
      </div>

      {/* <div>
          <label htmlFor="session">Search Session</label>

          <div className="flex items-end  gap-3">
            <div className="w-[500px]">
              <SelectInput placeholderText="Select Session" />
            </div>
            <Button version="secondary" text="Search Session" width="240px" />
          </div>
        </div> */}
      <div className="relative space-y-4">
        {schoolDetails?.emailVerified === false && (
          <div className="absolute w-full h-full top-0 left-0 z-10 bg-black/50 rounded-xl backdrop-blur-[2px] flex items-center justify-center">
            <span className="text-white font-extrabold text-2xl text-center">
              Verify your Email Id <br />
              to unlock features and services.
            </span>
          </div>
        )}
        {/* <div className="row school-dashbaord-cards">
          {[
            {
              name: "studentsRegistered",
              label: "Total Students Registered",
              value: participants?.length?.toString()?.padStart(2, "0"),
            },
            {
              name: "examinationsRegistered",
              label: "Total Examinations Registered",
              value: participants
                ?.reduce((acc, curr) => (acc += curr?.subjects?.length), 0)
                ?.toString()
                ?.padStart(2, "0"),
            },
            {
              name: "feeCollected",
              label: "Total Fee Collected",
            },
          ]?.map(({ name, label, value }) => (
            <div className="col-md-4" key={name}>
              <div className="card shadow-md text-[#34495E]">
                <h3 className="text-lg font-bold">{label}</h3>
                {name !== "feeCollected" ? (
                  !schoolDetails ? (
                    <div className="w-40 h-8 bg-gray-300 rounded-lg animate-pulse" />
                  ) : (
                    <div className="text-3xl font-bold">{value}</div>
                  )
                ) : !schoolDetails ? (
                  <div className="w-40 h-8 bg-gray-300 rounded-lg animate-pulse" />
                ) : (
                  <div className="text-3xl font-bold flex gap-2">
                    {totalFeeCollected?.totalFeeINR ? (
                      <span className="block">
                        ₹{" "}
                        {totalFeeCollected?.totalFeeINR
                          ?.toString()
                          ?.padStart(2, "0")}
                      </span>
                    ) : null}
                    {totalFeeCollected?.totalFeeINR &&
                    totalFeeCollected?.totalFeeUSD
                      ? " + "
                      : ""}
                    {totalFeeCollected?.totalFeeUSD ? (
                      <span className="block">
                        ${" "}
                        {totalFeeCollected?.totalFeeUSD
                          ?.toString()
                          ?.padStart(2, "0")}
                      </span>
                    ) : null}
                  </div>
                )}
                {schoolDetails &&
                  name === "studentsRegistered" &&
                  value > 0 && (
                    <Link
                      to="registeredStudents"
                      className="text-right hover:underline font-bold text-[#004EFD]"
                      onClick={() =>
                        GAEventTrack({
                          event: "school_dshbrd_view_registered_students",
                          data: {
                            uid: auth?.currentUser?.uid,
                            email: auth?.currentUser?.email,
                          },
                        })
                      }
                    >
                      View all
                    </Link>
                  )}
              </div>
            </div>
          ))}
        </div> */}
        <div className="school-dash-blue-card flex flex-column items-center justify-center">
          <div className="col-md-12 col-lg-9 left-blue-card w-full bg-[#DBF2F7] rounded-xl p-6">
            {/* Content Layout - Different for lg vs md/sm */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Text Content */}
              <div className="data flex flex-col justify-evenly flex-1">
                <p className="text-gray-700 text-base mb-2">
                  Share this link with the parents and students.
                </p>
                <div className="mb-4">
                  <p className="text-gray-700 text-base mb-2">
                    Our Regular Pricing:{" "}
                    <span className="text-gray-800">
                      ₹1499 for all subjects
                    </span>
                  </p>
                  <p className="text-gray-700 text-base">
                    <span className="font-bold text-green-600">
                      Special Academic Year Offer:
                    </span>{" "}
                    Every student who registers through your school link gets
                    full access at a{" "}
                    <span className="font-bold">one-time fee </span> of just{" "}
                    <span className="font-bold">
                      ₹{schoolDetails?.fee?.total} (₹
                      {schoolDetails?.fee?.practidoFee} + School fee: ₹
                      {schoolDetails?.fee?.schoolFee})
                    </span>{" "}
                    for this academic year.
                  </p>
                </div>
                <div className="link-tag flex items-center gap-4 justify-between bg-[#BDDEE5] p-3 px-4 rounded-lg mb-4">
                  {registrationCode ? (
                    <a
                      className="text-base w-full font-semibold text-[#004EFD]"
                      href={registrationCode}
                      target="_blank"
                      rel="noreferrer"
                      ref={regCodeRef}
                    >
                      {registrationCode}
                    </a>
                  ) : (
                    <span className="font-bold text-gray-600 text-lg animate-pulse px-10">
                      Loading ...{" "}
                    </span>
                  )}
                </div>
              </div>

              {/* QR Code - Hidden on md/sm, shown on lg */}
              <div
                ref={qrContainerRef}
                className="img-box relative bg-white rounded-xl border-2 border-gray-300 hidden lf:flex items-center justify-center lg:flex lg:w-auto lg:aspect-square lg:h-full"
                onMouseEnter={() => setQrHover(true)}
                onMouseLeave={() => setQrHover(false)}
              >
                <div
                  className={`${
                    qrHover ? "flex" : "hidden"
                  } absolute top-0 left-0 w-full h-full bg-black/30 rounded-xl z-10 gap-4 items-center justify-center`}
                >
                  <span className="p-2 bg-white/90 rounded-lg cursor-pointer">
                    <FiDownload className="w-6 h-6" onClick={downloadQRCode} />
                  </span>
                </div>
                <div
                  ref={qrRef}
                  className="p-4 svg-box w-full h-full flex items-center justify-center"
                >
                  <QRCodeSVG
                    value={registrationCode}
                    size={qrSize}
                    className="max-w-full max-h-full"
                  />
                </div>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex flex-wrap gap-3 mb-4">
              <button
                className={`flex items-center justify-center gap-2 p-2 px-3 rounded-xl w-[calc(50%-6px)] sm:w-auto order-1 sm:order-1 ${
                  copied
                    ? "bg-green-500 hover:bg-green-500/90 text-white"
                    : "bg-white"
                }`}
                style={
                  copied
                    ? { boxShadow: "0px 3px 0px rgba(50 150 50)" }
                    : { boxShadow: "0px 3px 0px rgba(156 163 175)" }
                }
                onClick={copyLink}
              >
                {copied ? <FaClipboardCheck className="w-4 h-4" /> : <IoCopy />}
                Copy Link
              </button>
              <button
                className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl text-sm transition-colors shadow-md w-[calc(50%-6px)] sm:w-auto order-3 sm:order-2"
                onClick={shareViaWhatsApp}
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
                WhatsApp
              </button>
              <button
                className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-xl text-sm transition-colors shadow-md w-[calc(50%-6px)] sm:w-auto order-4 sm:order-3"
                onClick={shareViaEmail}
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
                Email
              </button>
              <button
                className="flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-xl text-sm transition-colors shadow-md w-[calc(50%-6px)] sm:w-auto order-2 sm:order-4"
                onClick={shareLink}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                  />
                </svg>
                Share
              </button>
            </div>

            {/* QR Code for md/sm devices - shown below buttons */}
            <div
              ref={qrContainerRef}
              className="img-box relative bg-white rounded-xl border-2 border-gray-300 flex items-center justify-center
                       lg:hidden w-full h-64 sm:h-72 md:h-80 max-w-sm mx-auto"
              onMouseEnter={() => setQrHover(true)}
              onMouseLeave={() => setQrHover(false)}
            >
              <div
                className={`${
                  qrHover ? "flex" : "hidden"
                } absolute top-0 left-0 w-full h-full bg-black/30 rounded-xl z-10 gap-4 items-center justify-center`}
              >
                <span className="p-2 bg-white/90 rounded-lg cursor-pointer">
                  <FiDownload className="w-6 h-6" onClick={downloadQRCode} />
                </span>
              </div>
              <div
                ref={qrRef}
                className="p-4 svg-box w-full h-full flex items-center justify-center"
              >
                <QRCodeSVG
                  value={registrationCode}
                  size={qrSize}
                  className="max-w-full max-h-full"
                />
              </div>
            </div>
          </div>

          {/* Download Section */}
          <div className="col-md-12 col-lg-9 w-full bg-orange-50 border-l-4 border-orange-200 p-6 rounded-xl mt-4">
            <h4 className="text-gray-800 font-semibold text-lg mb-4">
              Download Practido - Olympiad Practice App
            </h4>
            <ul className="space-y-3 text-base text-gray-700 mb-6 ">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-4"></span>
                <span>
                  10,000+ installs, Teacher Approved by Google and a 4.5★ Play
                  Store and App Store rating
                </span>
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-4"></span>
                <span>
                  Unlimited practice in Math, Science, Computers & English
                  (Class 1–8)
                </span>
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-4"></span>
                <span>
                  Gamified challenges – trophies, leaderboard, points & rewards
                </span>
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-4"></span>
                <span>
                  Challenge your friends – make Olympiad practice exciting and
                  fun
                </span>
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-4"></span>
                <span>Instant results & progress tracking</span>
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-4"></span>
                <span>
                  Check solutions for every question – learn from mistakes
                  instantly
                </span>
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-4"></span>
                <span>Anytime, anywhere learning without extra workload</span>
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-4"></span>
                <span>
                  No waiting for the next book, no running out of material –
                  just practice & improvement
                </span>
              </li>
            </ul>
            <AppStoreButtons />
          </div>

          {/* Benefits Note Box */}
          <div className="col-md-12 col-lg-9 w-full bg-green-50 border-l-4 border-green-400 p-6 rounded-xl mt-4">
            <h4 className="text-green-800 font-semibold text-lg mb-4">
              Benefits for Your School
            </h4>
            <ul className="space-y-3 text-base text-gray-700">
              <li className="flex items-center">
                <span className="text-green-500 mr-4 text-xl">✓</span>
                <span>The next Olympiad topper could be from your school.</span>
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-4 text-xl">✓</span>
                <span>No charges for your school</span>
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-4 text-xl">✓</span>
                <span>Safe and secure access</span>
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-4 text-xl">✓</span>
                <span>Students start practicing right away</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div>
        <div>{/* other resources */}</div>
        <>{/* <DashboardInstructions placement="school" /> */}</>
      </div>
    </div>
  );
};

export default SchoolDashboard;
